"use client";

import React, { useState, useEffect } from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
const PER_PAGE = 12;
const FALLBACK_IMAGE = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1628443/img/counseling_blog_thumb.png";

// WordPressのタイトルに含まれる &#8211; などを文字に戻す
function decodeEntities(text: string) {
  return (text || "").replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n))).replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCharCode(parseInt(h, 16))).replace(/&quot;/g, '"').replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&");
}
// 表示するページ番号の並びを作る（最大7つ、多いときは … で省略）
function getPageItems(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({
    length: total
  }, (_, i) => i + 1);
  let start = Math.max(2, current - 1);
  let end = Math.min(total - 1, current + 1);
  if (current <= 3) {
    start = 2;
    end = 4;
  }
  if (current >= total - 2) {
    start = total - 3;
    end = total - 1;
  }
  const items: (number | "…")[] = [1];
  if (start > 2) items.push("…");
  for (let p = start; p <= end; p++) items.push(p);
  if (end < total - 1) items.push("…");
  items.push(total);
  return items;
}
// ページ指定欄（数字を入れてEnterか「移動」で該当ページへ）
function PageJumpInput({
  currentPage,
  totalPages,
  onJump
}: {
  currentPage: number;
  totalPages: number;
  onJump: (page: number) => void;
}) {
  const [value, setValue] = useState(String(currentPage));
  useEffect(() => {
    setValue(String(currentPage));
  }, [currentPage]);
  const toHalfWidth = (s: string) => s.replace(/[０-９]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xFEE0));
  const submit = () => {
    const n = parseInt(toHalfWidth(value.trim()), 10);
    if (Number.isInteger(n) && n >= 1 && n <= totalPages && n !== currentPage) {
      onJump(n);
    } else {
      setValue(String(currentPage));
    }
  };
  return <form onSubmit={e => {
    e.preventDefault();
    submit();
  }} className="flex items-center justify-center gap-2 text-sm text-muted-foreground font-default">
      <label htmlFor="blog-page-jump">ページ指定</label>
      <input id="blog-page-jump" type="text" inputMode="numeric" aria-label={`ページ番号（1〜${totalPages}）`} value={value} onChange={e => setValue(e.target.value)} onFocus={e => e.target.select()} className="h-9 w-14 rounded-md border border-primary/40 bg-secondary/30 text-center text-sm text-foreground cursor-text focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
      <span>/ {totalPages}</span>
      <Button type="submit" variant="outline" size="sm" className="ml-2 border-primary text-primary-heading hover:bg-primary/10 rounded-lg">移動</Button>
    </form>;
}
function BlogListConsumer({
  currentPage,
  totalPages,
  onPageChange
}: {
  currentPage: number;
  totalPages: number | null;
  onPageChange: (page: number) => void;
}) {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const handlePageChange = (newPage: number) => {
    onPageChange(newPage);
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4 animate-pulse">
            <div className="w-full aspect-video bg-muted rounded-lg" />
            <div className="h-6 w-3/4 bg-muted rounded" />
            <div className="h-4 w-24 bg-muted rounded" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center text-muted-foreground py-12">
        <p>ブログ記事の読み込み中にエラーが発生しました。</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center text-muted-foreground py-12">
        <p>現在、表示できるブログ記事はありません。</p>
      </div>;
  }
  const hasPrev = currentPage > 1;
  const hasNext = totalPages !== null ? currentPage < totalPages : posts.length === PER_PAGE;
  return <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
        {posts.map((post, i) => <article key={post.id} data-index={i} className="flex flex-col">
            <Link to={post.link || "#"} className="block" aria-hidden="true" tabIndex={-1}>
              <img src={post.featuredImage || FALLBACK_IMAGE} loading="lazy" alt="" className="w-full aspect-video object-cover rounded-lg" />
            </Link>
            <div className="flex flex-col flex-grow pt-4">
              <Link to={post.link || "#"} className="block mb-3">
                <h3 className="text-lg font-default font-bold text-foreground leading-snug hover:text-primary-heading transition-colors line-clamp-3">{decodeEntities(post.title)}</h3>
              </Link>
              <div className="mt-auto">
                {post.date ? <time dateTime={post.date} className="text-sm text-muted-foreground">{new Date(post.date).toLocaleDateString("ja-JP", {
                year: "numeric",
                month: "long",
                day: "numeric"
              })}</time> : null}
              </div>
            </div>
          </article>)}
      </div>

      {(hasPrev || hasNext) && <nav aria-label="ページ送り" className="mt-16 flex flex-col items-center gap-6">
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6">
            <div className="w-full sm:w-auto order-first sm:order-none flex justify-center items-center gap-1">
              {totalPages !== null ? getPageItems(currentPage, totalPages).map((item, i) => item === "…" ? <span key={`gap-${i}`} className="w-8 text-center text-muted-foreground">…</span> : item === currentPage ? <span key={item} aria-current="page" className="h-10 w-10 flex items-center justify-center rounded-lg bg-primary text-primary-foreground text-sm font-bold">{item}</span> : <Button key={item} variant="ghost" onClick={() => handlePageChange(item)} className="h-10 w-10 p-0 rounded-lg text-sm text-primary-heading hover:bg-primary/10">{item}</Button>) : <span className="text-sm text-muted-foreground font-default">{currentPage}</span>}
            </div>
            <Button variant="outline" onClick={() => handlePageChange(currentPage - 1)} disabled={!hasPrev} className="sm:order-first border-primary text-primary-heading hover:bg-primary/10 px-6 rounded-lg disabled:opacity-40">
              前のページ
            </Button>
            <Button variant="outline" onClick={() => handlePageChange(currentPage + 1)} disabled={!hasNext} className="border-primary text-primary-heading hover:bg-primary/10 px-6 rounded-lg disabled:opacity-40">
              次のページ
            </Button>
          </div>
          {totalPages !== null && totalPages > 7 && <PageJumpInput currentPage={currentPage} totalPages={totalPages} onJump={handlePageChange} />}
        </nav>}
    </>;
}
export default function BlogList() {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);

  // 総ページ数は WordPress が返す X-WP-TotalPages ヘッダーから取得する
  useEffect(() => {
    fetch(`https://yotsuya-sc-room.com/wp-json/wp/v2/posts?per_page=${PER_PAGE}&_fields=id`).then(res => {
      const total = Number(res.headers.get("X-WP-TotalPages"));
      if (total > 0) setTotalPages(total);
    }).catch(() => {});
  }, []);
  return <section data-section-id="257670" id="blog-posts-grid" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-primary font-default leading-tight mb-4">ブログ</h1>
          <Separator className="w-12 h-0.5 bg-primary mb-8" />
          <p className="text-foreground leading-relaxed font-default">心のケアや日々の気づき、カウンセリングに関する情報を発信しています。</p>
        </div>

        <PostsProvider key={currentPage} wp_query={{
        post_type: "post",
        per_page: PER_PAGE,
        orderby: "date",
        order: "desc",
        page: currentPage
      }}>
          <BlogListConsumer currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </PostsProvider>
      </div>
    </section>;
}